/**
 * CookieConsent — GDPR/CCPA compliant cookie consent banner.
 *
 * Fixed bottom banner that asks the user for cookie consent.
 * Persists choice in localStorage. Calls updateConsent() from analyticsService
 * so GA4 Consent Mode v2 is updated immediately.
 */

import { useState, useEffect } from 'react';
import { updateConsent } from '../../services/analyticsService';
import './CookieConsent.css';

const STORAGE_KEY = 'cookie_consent';

type ConsentChoice = 'accepted' | 'declined';

function getStoredChoice(): ConsentChoice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'accepted' || value === 'declined') return value;
    return null;
  } catch {
    return null;
  }
}

function storeChoice(choice: ConsentChoice): void {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // localStorage unavailable — fail silently
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = getStoredChoice();
    if (existing) {
      // Re-apply stored consent on mount so GA4 picks it up
      updateConsent(existing === 'accepted');
    } else {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    storeChoice('accepted');
    updateConsent(true);
    setVisible(false);
  };

  const handleDecline = () => {
    storeChoice('declined');
    updateConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent" role="dialog" aria-label="Cookie consent">
      <div className="cookie-consent__content">
        <p className="cookie-consent__text">
          We use cookies and similar technologies to analyze site usage and improve
          your experience. You can accept or decline non-essential cookies. Your
          choice is stored locally and can be changed at any time in Settings.
          For more details, see our{' '}
          <a href="/privacy" className="cookie-consent__link">
            Privacy Policy
          </a>
          .
        </p>
        <div className="cookie-consent__actions">
          <button
            className="cookie-consent__button cookie-consent__button--decline"
            onClick={handleDecline}
          >
            Decline
          </button>
          <button
            className="cookie-consent__button cookie-consent__button--accept"
            onClick={handleAccept}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
